import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import AddLinkRoundedIcon from '@mui/icons-material/AddLinkRounded'
import { useNavigate } from 'react-router-dom'
import { adminService, type PersonProfile } from '../../services/adminService'
import ProfileAvatar from '../../components/ProfileAvatar'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

/**
 * Admin People / Profiles: browse the durable cross-event person profiles and
 * link additional emails to a profile (merge) when someone registered under a
 * different email at another event.
 */
export default function AdminPeoplePage() {
  const { notify } = useToast()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<PersonProfile[]>([])
  const [loading, setLoading] = useState(true)

  const load = (query?: string) => {
    setLoading(true)
    adminService
      .listPersonProfiles(query)
      .then(setRows)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/admin')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        People
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cross-event attendee profiles. Each person fills in their photo/avatar,
        bio, and headline once; it's reused at every event they attend. Link a
        second email to a profile if someone registered under a different address.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by name or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(q)}
        />
        <Button variant="contained" onClick={() => load(q)}>
          Search
        </Button>
      </Stack>

      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No profiles yet"
          description="Profiles appear here once attendees complete onboarding."
        />
      ) : (
        <Stack spacing={1.5}>
          {rows.map((p) => (
            <PersonCard key={p.id} person={p} onChanged={() => load(q)} />
          ))}
        </Stack>
      )}
    </Container>
  )
}

function PersonCard({
  person,
  onChanged,
}: {
  person: PersonProfile
  onChanged: () => void
}) {
  const { notify } = useToast()
  const [aliasEmail, setAliasEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const link = async () => {
    if (!aliasEmail.trim()) return
    setBusy(true)
    try {
      await adminService.addPersonAlias(person.id, aliasEmail.trim())
      notify('Email linked')
      setAliasEmail('')
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const unlink = async (aliasId: string, email: string) => {
    if (!(await confirmDelete(`Unlink ${email}?`))) return
    try {
      await adminService.removePersonAlias(person.id, aliasId)
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const remove = async () => {
    if (
      !(await confirmDelete(
        `Delete the profile for ${person.fullName || person.email}?\n\n` +
          'Their saved photo/avatar, bio, and headline will be removed. They ' +
          'will be asked to set up their profile again the next time they sign in.',
      ))
    )
      return
    setBusy(true)
    try {
      await adminService.deletePersonProfile(person.id)
      notify('Profile deleted')
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <ProfileAvatar avatar={person} name={person.fullName} size={48} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
              {person.fullName || person.email}
            </Typography>
            <IconButton
              size="small"
              color="error"
              disabled={busy}
              onClick={remove}
              aria-label="Delete profile"
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {person.email}
            {person.headline ? ` · ${person.headline}` : ''}
          </Typography>
          {person.bio && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {person.bio}
            </Typography>
          )}

          {person.aliases.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {person.aliases.map((a) => (
                <Chip
                  key={a.id}
                  label={a.email}
                  size="small"
                  onDelete={() => unlink(a.id, a.email)}
                  deleteIcon={<DeleteRoundedIcon />}
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <TextField
              size="small"
              placeholder="Link another email…"
              value={aliasEmail}
              onChange={(e) => setAliasEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && link()}
            />
            <IconButton color="primary" disabled={busy || !aliasEmail.trim()} onClick={link}>
              <AddLinkRoundedIcon />
            </IconButton>
          </Stack>
        </Box>
      </Stack>
    </Card>
  )
}
